import React from 'react';
import {FormControl, IconButton, InputLabel, makeStyles, MenuItem, Select, Tab, Tabs, TextField, useMediaQuery} from '@material-ui/core';
import {useTheme} from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import PropTypes from 'prop-types';
import languages from "./constants";
import {rhymeTypesFor} from "./rhymeTypes";

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: '12px',
    },
    textField: {
        flex: '1 1 0',
        minWidth: 0,
        marginTop: '0px',
        marginBottom: '0px',
        marginRight: '8px',
    },
    langSelect: {
        minWidth: 160,
        marginRight: '8px',
        // On phones the language picker gets its own row below the search box
        // so the search term keeps a usable width.
        [theme.breakpoints.down('xs')]: {
            order: 3,
            flexBasis: '100%',
            marginRight: 0,
            marginTop: theme.spacing(1.5),
        },
    },
    button: {
        height: 56,
        width: 56,
    },
    tabs: {
        marginTop: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tab: {
        minWidth: 0,
        textTransform: 'none',
        fontSize: '0.95rem',
        padding: theme.spacing(1, 1),
    },
}));

function RhymeForm(props) {
    const classes = useStyles();
    // No ThemeProvider in this app: useTheme() from core/styles falls back to
    // the default theme, while a callback to useMediaQuery would get null.
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('xs'));
    const {
        word,
        language,
        rhymeType,
        onWordChange,
        onLanguageChange,
        onRhymeTypeChange,
        onGetRhymes,
        isLoading
    } = props;

    return (
        <div>
            <form
                className={classes.form}
                role="search"
                onSubmit={(event) => {
                    event.preventDefault();
                    onGetRhymes();
                }}
            >
                <TextField
                    id="search-term"
                    label="Enter a word"
                    variant="outlined"
                    margin="normal"
                    value={word}
                    onChange={onWordChange}
                    className={classes.textField}
                    inputProps={{
                        enterKeyHint: 'search',
                        autoCapitalize: 'none',
                        autoCorrect: 'off',
                        spellCheck: false,
                    }}
                />
                <FormControl variant="outlined" className={classes.langSelect}>
                    <InputLabel id="language-label">Language</InputLabel>
                    <Select
                        labelId="language-label"
                        label="Language"
                        value={language}
                        onChange={(event) => {
                            onLanguageChange(event.target.value);
                        }}
                    >
                        {languages.map((lang) => (
                            <MenuItem value={lang.code} key={lang.code}>
                                {lang.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <IconButton
                    type="submit"
                    color="primary"
                    disabled={isLoading}
                    className={classes.button}
                    aria-label="Find rhymes"
                >
                    <SearchIcon/>
                </IconButton>
            </form>
            <Tabs
                value={rhymeType}
                onChange={(event, value) => onRhymeTypeChange(value)}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                aria-label="Rhyme type"
                className={classes.tabs}
            >
                {rhymeTypesFor(language).map((type) => (
                    <Tab
                        key={type.id}
                        value={type.id}
                        label={isPhone ? type.shortLabel : type.label}
                        aria-label={type.label}
                        className={classes.tab}
                        id={`rhyme-type-tab-${type.id}`}
                        aria-controls="rhyme-results"
                    />
                ))}
            </Tabs>
        </div>
    );
}

RhymeForm.propTypes = {
    word: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    rhymeType: PropTypes.string.isRequired,
    onWordChange: PropTypes.func.isRequired,
    onLanguageChange: PropTypes.func.isRequired,
    onRhymeTypeChange: PropTypes.func.isRequired,
    onGetRhymes: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
};

export default RhymeForm;
